// TODO: 修改文件名
import qs from 'qs';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import Modal from '@tencent/dui/lib/components/Modal';
import i18n from '@i18next/i18n';
import ua from '@tencent/docs-user-agent';
import { getQuery } from 'sutils';

import FullscreenHandler from '@utils/fullscreen/fullscreen-handler';
import { ROLE, MEETING_SCENE_SOURCE } from '@src/page/toc/common/meeting-room-manager/message_const';
import { endPlay } from '@src/framework/player/src/actions/control';
import store, { registerHooks, deregisterHooks } from '@player/src/store';
import { laserBoardEventBus, laserBoardEventsKeys } from './event-bus-instance';
import { openPopupWindow, focusPopupWindow } from './lib/popup-window';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

import { PlayerService } from './service/player';
import { MainPlayerService } from './service/main-player';
import { PlayerEventsManager } from '@src/js/common/ark-extensions/dc-components/wcjs-video-player/events-manager';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { MessageProtocol } from '@src/slide/infrastructure/module/independent/windows-message-protocol';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';

let childWindow: Window | null = null;
let childWindowName: string | null = null;

if (ENV.IS_PC_LOCAL_EDIT) {
    // 重写document.domain 如果不重写一下，虽然域一致，跨iframe取值时也会遇到跨域错误
    // eslint-disable-next-line no-self-assign
    document.domain = document.domain.split('.').slice(-3)
        .join('.');
}

export let destoryPresenterViewWindow = (): void => {};

/**
 * 打开独立的演讲者视图弹窗
 * @param docId
 * @param startPage 播放页数
 * @param isInstant 是否在 player 启动时立即打开演讲者视图窗口
 */
export async function openPresenterViewWindow(docId: string, isInstant = false): Promise<void> {
    const { isFullscreen } = FullscreenHandler.getInstance();
    // 全屏与弹窗冲突，先主动退出全屏
    // 注意即使已存在弹窗，也需要先退出全屏；open/focus 子窗都影响全屏；
    if (isFullscreen) {
        // 与 player 点击链接跳转行为相同，设置 flag 防止退出全屏时触发结束播放；
        // TODO: 此处沿用旧的 magic string，语义也不明确，需要收敛
        eventBus.emit(EventKeys.LINK_CLICKED);
        FullscreenHandler.getInstance().exitFullscreen();

        // 等待退出全屏后再 open/focus window
        // FIXME: 下个提交重构 FullscreenHandler 后提供相应接口
        await delay(1500);
    }

    if (childWindow?.closed === false) {
        focusPopupWindow(childWindow, childWindowName!);
        return;
    }

    // 全屏状态下，需要确保退出全屏后再打开弹窗，导致`window.open`的调用是异步的
    // 此时 safari 将认为该行为不是由用户操作发起，弹窗会被 block
    // 故此处只退出全屏，提示用户再次点击打开
    if (ua.isSafari && isFullscreen) {
        DUISnackbar.show({
            // 设置 id 确保每次 snackbar 调用互斥（即新的调用会销毁上一个）
            id: 'PRESENTER_VIEW_SNACK',
            message: i18n.t('因safari浏览器限制，弹窗被阻止。请再次点击备注视图打开'),
            type: 'success',
            duration: 3000,
            autoClose: true,
            closable: true,
        });
        return;
    }

    const query = {
        docId,
        role: ROLE.MASTER,
        sceneSource: MEETING_SCENE_SOURCE.Local,
        isPresenterView: 1,
        isSubsidiary: 1,

        // getQuery 的 key 不存在时也会返回 "", 这里需要处理为 undefined, 让 qs.stringify 丢弃该 key
        local_edit: getQuery('local_edit') || undefined,
        magic: getQuery('magic') || undefined,
        dver: SlideDataServices.ClientVarsService()?.getClientVars().collab_client_vars?.dver,
        editorState: (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.userPriviledgeService.pptEditorState,
    };

    const url = `${location.origin}/meetingroom/slide/player?${qs.stringify(query)}`;
    childWindowName = `PRESENTER_VIEW_${docId}`;
    childWindow = openPopupWindow(url, childWindowName, { width: 800, height: 520 });

    if (!childWindow) return;

    if (isInstant) {
        // 桌面端会自动将观众视图移动到另一屏幕，无需弹窗提示
        if (!ua.isElectronTencentDocsClient) {
            // 直接进入演讲者视图，弹窗提示
            Modal.confirm({
                title: i18n.t('观众视图'),
                content: i18n.t('请把观众视图拖拽到观众可看到的屏幕上。'),
                okText: i18n.t('确定'),
                cancelText: null,
            });
        }
    } else {
        // 从正常播放弹出备注视图窗口, toast
        DUISnackbar.show({
            // 设置 id 确保每次 snackbar 调用互斥（即新的调用会销毁上一个）
            id: 'PRESENTER_VIEW_SNACK',
            message: i18n.t('已新开备注视图窗口，请切换查看'),
            type: 'success',
            duration: 3000,
            autoClose: true,
            closable: true,
        });
    }

    // 注册己方服务
    const deregisterService = MessageProtocol.getInstance().registerService(MainPlayerService);
    // 创建 targetWindow 服务代理
    const serviceProxy = MessageProtocol.getInstance().createServiceProxy(childWindow, PlayerService);
    // hook redux, 同步 action 到 targetWindow
    registerHooks({ beforeAction: action => serviceProxy.dispatchAction(action) });
    // 同步激光笔事件到 targetWindow
    const removeCursorEvent = laserBoardEventBus
        .on(laserBoardEventsKeys.CUSOR_MOVE, position => serviceProxy.syncMousemove(position));
    getMediaSyncManager().promise()
        .then((ins) => {
            ins.initServiceProxy(serviceProxy);
        });

    destoryPresenterViewWindow = () => {
        if (!childWindow || childWindow.closed) return;

        childWindow.close();
        childWindow = null;
        childWindowName = null;
        window.removeEventListener('unload', destoryPresenterViewWindow);
        destoryPresenterViewWindow = () => {};

        deregisterService();
        serviceProxy.destory();
        deregisterHooks();
        removeCursorEvent();
        getMediaSyncManager().promise()
            .then((ins) => {
                ins.destroy();
            });
    };

    window.addEventListener('unload', destoryPresenterViewWindow);

    // 接收子窗口close事件,通知主窗口退出播放态(主窗口退出播放态会destroy本地播放器相关逻辑)
    const endplayHandler = ({ data }) => {
        if (data?.type === 'PRESENTER_VIEW_CLOSE' && PlayerEventsManager.playerEventsExist()) {
            store.dispatch(endPlay());
            window.removeEventListener('message', endplayHandler);
        }
    };
    window.addEventListener('message', endplayHandler);
}

const getMediaSyncManager = () => (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
    .viewableServiceLayer.noteViewMediaSyncManager;

const delay = ms => new Promise(res => setTimeout(res, ms));
